// 앱 전역 시각 표시 규약. wire 파싱 규칙은 shared base의 `parseUtcMs`가 소유한다. 이 모듈은
// 페이지 호출부를 위해 그 파서를 표시 함수와 함께 다시 내보낸다. 파싱한 instant는 ms 정수로만
// 전달한다. `Date` 객체는 이 표시 모듈 밖으로 나가지 않는다.
// 호출부의 `formatDateTime(parseUtcMs(iso))` 는 길어 보인다. 하지만 이 형태면 "이 값이 파서를
// 거쳤는가" 를 한 줄만 읽고 확인할 수 있다.
//
// 절대 시각은 로케일에 맡기지 않고 숫자 24시간 형식으로 고정한다. 이유는 다음과 같다.
//   - 앱은 페이지마다 한국어와 영어가 섞여 있어 로케일 정책 하나를 정할 수 없다.
//   - 로케일 출력은 폭이 일정하지 않아 하드코딩된 표 컬럼 폭과 충돌한다.
//   - ICU 버전이 바뀌면 문자열이 바뀐다(Node 24 에서 ko-KR 의 `오후`가 `PM` 으로).
//     그러면 정확한 문자열 단언이 깨진다.

import { parseUtcMs } from "@/shared/utils/datetime/public";

export { parseUtcMs };

const EMPTY = "—";

function pad2(value: number): string {
  return String(value).padStart(2, "0");
}

function isRenderable(ms: number | null): ms is number {
  return ms !== null && Number.isFinite(ms);
}

function datePart(date: Date): string {
  return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`;
}

/** 목록·상세의 기본 시각 표시. 예) `2026-06-05 21:34` (뷰어 타임존). */
export function formatDateTime(ms: number | null): string {
  if (!isRenderable(ms)) {
    return EMPTY;
  }
  const date = new Date(ms);
  return `${datePart(date)} ${pad2(date.getHours())}:${pad2(date.getMinutes())}`;
}

/** 좁은 자리의 최근 시각 표시. 연도를 뺀다. 예) `06-05 21:34` (뷰어 타임존). */
export function formatMonthDayTime(ms: number | null): string {
  if (!isRenderable(ms)) {
    return EMPTY;
  }
  const date = new Date(ms);
  return `${pad2(date.getMonth() + 1)}-${pad2(date.getDate())} ${pad2(date.getHours())}:${pad2(date.getMinutes())}`;
}

/** 날짜만 필요한 자리. 예) `2026-06-05` (뷰어 타임존). */
export function formatDate(ms: number | null): string {
  return isRenderable(ms) ? datePart(new Date(ms)) : EMPTY;
}

/**
 * 툴팁·운영 화면용 정밀 표시. 예) `2026-06-05 21:34:56 (UTC+09:00)`.
 *
 * 뷰어의 UTC offset 을 함께 적는다. 서울과 us-south1 에서 같은 워커 heartbeat 을 보는 두 사람은
 * 누구의 시계를 읽는지 알아야 값을 대조할 수 있다. 이 함수는 offset 을 **표시**만 한다.
 * offset 산술은 하지 않는다. 이 앱은 offset 산술을 하지 않는다.
 */
export function formatDateTimeSeconds(ms: number | null): string {
  if (!isRenderable(ms)) {
    return EMPTY;
  }
  const date = new Date(ms);
  const clock = `${pad2(date.getHours())}:${pad2(date.getMinutes())}:${pad2(date.getSeconds())}`;
  // getTimezoneOffset 은 "UTC 기준으로 로컬이 뒤처진 값" 이므로 부호가 반대다.
  const offsetMinutes = -date.getTimezoneOffset();
  const sign = offsetMinutes < 0 ? "-" : "+";
  const abs = Math.abs(offsetMinutes);
  const offset = `${pad2(Math.floor(abs / 60))}:${pad2(abs % 60)}`;
  return `${datePart(date)} ${clock} (UTC${sign}${offset})`;
}

/**
 * 대략적인 길이. 예) `12s` / `3m 5s` / `1h 2m`.
 *
 * 폭 제한이 없는 상태 줄에 쓴다. 시 단위에서는 초가 뜻이 없으므로 초를 버린다.
 * 분 단위에서는 초를 남긴다. 짧은 작업에서는 초 차이가 중요한 정보다.
 */
export function formatDuration(ms: number | null): string {
  if (!isRenderable(ms)) {
    return EMPTY;
  }
  const totalSeconds = Math.max(0, Math.floor(ms / 1000));
  if (totalSeconds < 60) {
    return `${totalSeconds}s`;
  }
  const minutes = Math.floor(totalSeconds / 60);
  if (minutes < 60) {
    return `${minutes}m ${totalSeconds % 60}s`;
  }
  return `${Math.floor(minutes / 60)}h ${minutes % 60}m`;
}

/**
 * 스톱워치. 예) `00:05:42` / `100:05:06`.
 *
 * `formatDuration` 과 따로 두는 이유는 폭이다. 사용자는 열을 따라 값을 비교한다. 그래서
 * 자릿수가 고정되어야 하고, 1초마다 갱신되어도 좌우로 흔들리지 않아야 한다. 하루를 넘기면
 * 시 자리가 두 자리를 넘는다. 이 값은 자르지 않는다.
 */
export function formatClock(ms: number | null): string {
  if (!isRenderable(ms)) {
    return EMPTY;
  }
  const totalSeconds = Math.max(0, Math.round(ms / 1000));
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  return `${pad2(hours)}:${pad2(minutes)}:${pad2(totalSeconds % 60)}`;
}

const MINUTE_MS = 60_000;
// 롤링 창(최근 N일)처럼 길이를 ms 로 계산하는 호출부가 같은 값을 다시 적지 않도록 내보낸다.
export const HOUR_MS = 60 * MINUTE_MS;
export const DAY_MS = 24 * HOUR_MS;

// 큰 단위부터 비교한다. 초 단위는 없다. 1분 미만은 모두 `now` 다.
const RELATIVE_UNITS: readonly (readonly [number, string])[] = [
  [365 * DAY_MS, "y"],
  [30 * DAY_MS, "mo"],
  [DAY_MS, "d"],
  [HOUR_MS, "h"],
  [MINUTE_MS, "m"],
];

/**
 * 지금 기준 상대 시각. 예) `now` / `5m ago` / `2h ago` / `3d ago` / `in 5m`.
 *
 * `Intl.RelativeTimeFormat` 은 검토했지만 쓰지 않는다. 이유는 다음과 같다.
 *   - 영어 narrow 출력이 이 구현과 글자까지 같아서 얻는 것이 없다.
 *   - ICU 버전에 따라 바뀌는 문자열을 정확한 문자열 단언에 가져온다.
 *   - 이 앱에 아직 없는 로케일 정책을 요구한다.
 * 구현이 이 함수 하나뿐이므로, 정책이 생기면 그때 교체한다.
 *
 * 1분 미만은 `now` 다. 서울과 us-south1 사이의 시계 오차는 작은 음수 차이를 만든다. 그 차이를
 * 미래로 표시하는 것보다 `now` 가 정확하다. 반대로 1분 이상 미래는 `in 5m` 처럼 그대로
 * 표시한다. 0 으로 합치면 실제 데이터 문제가 화면에서 보이지 않는다.
 */
export function formatRelativeTime(timestampMs: number | null, nowMs: number = Date.now()): string {
  if (!isRenderable(timestampMs) || !Number.isFinite(nowMs)) {
    return EMPTY;
  }
  const diff = nowMs - timestampMs;
  const abs = Math.abs(diff);
  const unit = RELATIVE_UNITS.find(([threshold]) => abs >= threshold);
  if (unit === undefined) {
    return "now";
  }
  const [threshold, suffix] = unit;
  const value = Math.floor(abs / threshold);
  return diff >= 0 ? `${value}${suffix} ago` : `in ${value}${suffix}`;
}
